<template>
  <div
    v-if="option"
    class="delivery-option-component"
    :class="{
      selected: optionValue === deliveryOption
    }"
    @click="optionSelected"
    @keydown.enter="optionSelected"
    tabindex="0"
  >
    <pn-radio-button
      :value="optionValue"
      name="delivery-option"
      :checked="optionValue === deliveryOption"
      @click="optionSelected"
    ></pn-radio-button>

    <div class="option-wrapper">
      <div class="calendar-info">
        <h2 v-if="isBusinessUser">
          {{ $t(option.name) }}
        </h2>
        <h2 v-else>{{ $t('W2P_LETTER_NOT_FIRSTCLASS') }}</h2>
        <p class="small" v-if="showDescription">
          {{ $t(option.deliveryDays[0].description) }}
        </p>
        <div class="top">
          <div class="left-price" v-if="hasDomesticAddresses">
            <p class="small bold">
              {{ priceFormatter(domesticPrice) }}
              {{ $t(product.pricingInfo.units) }}
            </p>
            <p class="small grey">
              /{{ $t(product.pricingInfo.piece) }}
              {{ $t('W2P_DOMESTIC') }}
            </p>
          </div>
          <div class="left-price" v-if="hasInternationalAddresses">
            <p class="small bold">
              {{ priceFormatter(internationalPrice) }}
              {{ $t(product.pricingInfo.units) }}
            </p>
            <p class="small grey">
              /{{ $t(product.pricingInfo.piece) }}
              {{ $t('W2P_INTERNATIONAL') }}
            </p>
          </div>
        </div>
      </div>

      <div class="date-card">
        <p>
          {{ deliveryDate.format('MMM') }}
        </p>
        <div class="card-content">
          <h1>
            {{ deliveryDate.format('DD') }}
          </h1>
          <p v-if="hasInternationalAddresses" class="star">*</p>
        </div>
      </div>

      <div class="price-container">
        <p class="bold top">
          {{
            priceFormatter(getDeliveryPrice(option), true) +
              ' ' +
              $t(product.pricingInfo.units)
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment-business-days'
import _ from 'lodash'
import { priceFormatter } from '@/helpers/priceFormatter'

export default {
  props: {
    product: {
      type: Object,
      required: true
    },
    option: {
      type: Object,
      required: true
    },
    selectedDate: {
      type: String,
      required: false
    },
    showDescription: {
      type: Boolean,
      required: false
    },
    isCustomOption: {
      type: Boolean,
      required: false
    }
  },
  computed: {
    ...mapState(['accessToken', 'market', 'lang']),
    ...mapState('letter/address', ['recipientsData']),
    ...mapState('letter/delivery', ['deliveryOption']),
    ...mapGetters(['isBusinessUser']),
    domesticPrice() {
      let total = 0
      if (this.option.hasLateHandInTime && this.option.additionalServices[0]) {
        total =
          this.option.postageProducts[0].price_model.per_piece +
          this.option.additionalServices[0].price
      } else {
        total = this.option.postageProducts[0].price_model.per_piece
      }
      return total / 100
    },
    internationalPrice() {
      let total = 0
      if (this.option.hasLateHandInTime && this.option.additionalServices[0]) {
        total =
          this.option.postageProducts[1].price_model.per_piece +
          this.option.additionalServices[0].price
      } else {
        total = this.option.postageProducts[1].price_model.per_piece
      }
      return total / 100
    },
    deliveryDate() {
      let deliveryDate = moment(this.selectedDate)
      const start = this.option.deliveryDays[0].start
      const end = this.option.deliveryDays[0].end

      if (!this.isCustomOption) {
        if (end) {
          deliveryDate = deliveryDate.businessAdd(end)
        } else {
          deliveryDate = deliveryDate.businessAdd(start)
        }
      }

      return deliveryDate.locale(this.lang)
    },
    optionValue() {
      return this.deliveryDate.format('YYYY-MM-DD') + '-' + this.option.id
    },
    hasDomesticAddresses() {
      return this.recipientsData[0]
    },
    hasInternationalAddresses() {
      if (
        this.recipientsData[1] ||
        this.recipientsData[2] ||
        this.recipientsData[3]
      ) {
        return true
      }
      return false
    }
  },
  methods: {
    getDeliveryPrice(deliveryOption) {
      //calculate sum for all postages and late hand-in (if included)
      let total = 0
      let additionalServices = 0
      total += _.sumBy(deliveryOption.pricing.postage, postage => {
        return postage.price / 100
      })

      if (deliveryOption.pricing.additionalServices) {
        let lateHandIn = this.getLateHandIn(
          deliveryOption.pricing.additionalServices
        )

        if (lateHandIn) {
          additionalServices += lateHandIn.price / 100
        }

        total += additionalServices
      }
      return total
    },
    getLateHandIn(additionalServices) {
      return additionalServices.find(item => {
        //very unsafe, name can change
        return item.name === 'W2P_LATEHANDINTIME_NAME'
      })
    },
    priceFormatter,
    optionSelected() {
      const deliveryDate = this.deliveryDate.format('YYYY-MM-DD')
      const deliveryId = this.option.id
      const value = this.optionValue

      this.$emit('optionSelected', {
        deliveryDate,
        deliveryId,
        value
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.delivery-option-component {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 2em 0 0 0;
  padding: 0 1em;
  border: 0.1rem solid $gray200;
  transition: background-color 0.2s ease-in-out, border 0.2s ease-in-out;

  &.selected {
    border: 0.1rem solid $blue700;
    background-color: $blue50;

    .price-container {
      border-left: 0.1rem solid $blue700;
      white-space: nowrap;
    }
  }

  &:hover {
    cursor: pointer;
  }
}

.grey {
  color: $gray700;
}

.option-wrapper {
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
}

pn-radio-button {
  display: flex;
  margin-bottom: 0;
}

.star {
  color: $black !important;
  vertical-align: text-top;
  font-weight: 300;
}

.calendar-info {
  padding-right: 1em;
  h2 {
    margin-bottom: 0.2em;
    color: $black;
  }

  .top {
    margin-top: 0.7em;
  }
}

.left-price {
  p {
    display: inline;
  }
}

.date-card {
  min-width: 5.5em;
  height: 6em;
  margin-left: auto;
  background-color: $blue700;
  color: $white;
  p {
    padding: 0.2em;
    text-align: center;
    color: $white;
  }
  .card-content {
    background-color: $white;
    color: $black;
    height: 70%;
    padding: 0.3em 0.2em 0.2em 0.2em;
    width: 85%;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.price-container {
  padding: 2.7em 0 2em 1em;
  border-left: 0.1rem solid $gray200;
  height: 100%;
  margin-left: 1em;
  text-align: center;
  transition: border 0.2s ease-in-out;

  .top {
    margin-bottom: 1em;
  }
}

@media screen and (max-width: 50em) {
  .delivery-option-component {
    &.selected {
      border: 0.1rem solid $blue700;
      background-color: $blue50;

      .price-container {
        border-top: 0.1rem solid $blue700;
        border-left: none;
      }
    }
  }

  .option-wrapper {
    flex-direction: column;
    align-items: flex-start;
    padding: 1em;
  }

  .date-card {
    margin: 0;
    order: 2;
  }

  .price-container {
    order: 3;
    margin: 1em 0 0;
    border: none;
    width: 100%;
    text-align: left;
    border-top: 0.1rem solid $gray200;
    padding: 1em 0 0;
  }

  .left-price:last-child {
    margin-bottom: 1em;
  }

  .price-container .top {
    margin-bottom: 0;
  }
}
</style>
